import { GRID_SIZE } from '../constants';

export const SDM_GRID_ROWS = 3;
export const SDM_GRID_COLS = 3;

/** When grouping stored widgets into rows, y values within this band are one row. */
const ROW_Y_TOLERANCE = 1;

export interface GridPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Legacy default row shapes (1–5 widgets) before the flexible grid. Kept for
 * templates and for interpreting saved layouts: 5 widgets are 1/2/2, not 3/2.
 * Heights are equalized below; only the row membership is preserved here.
 */
const LEGACY_DEFAULT_ROW_SHAPES: ReadonlyArray<readonly number[]> = [
  [1],
  [1, 1],
  [1, 2],
  [1, 2, 1],
  [1, 2, 2],
];

/**
 * Default widget placement for a layout with `count` widgets.
 *
 * For 1–5 widgets, uses the legacy row shapes (e.g. 5 → 1/2/2) with equal row
 * heights. For 6–9, falls back to row-major fill (3/3/3, etc.).
 *
 * Row height always splits evenly across however many rows are in use; widget
 * width always splits evenly across however many widgets share its row. Returns
 * positions in add-order (position[i] is where the i-th widget added lands).
 */
export function computeDefaultPositions(count: number): GridPosition[] {
  if (count <= 0) return [];

  const legacyShape = LEGACY_DEFAULT_ROW_SHAPES[count - 1];
  if (legacyShape) {
    const numRows = legacyShape.length;
    const height = GRID_SIZE / numRows;
    const positions: GridPosition[] = [];

    for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
      const cols = legacyShape[rowIndex];
      const width = GRID_SIZE / cols;
      for (let col = 0; col < cols; col++) {
        positions.push({ x: col * width, y: rowIndex * height, w: width, h: height });
      }
    }
    return positions;
  }

  return computeRowMajorPositions(count);
}

/**
 * Row-major fill for 6+ widgets (or when explicitly needed): fills row 0
 * left-to-right up to SDM_GRID_COLS, then row 1, then row 2. So 7 are 3/3/1.
 */
export function computeRowMajorPositions(count: number): GridPosition[] {
  if (count <= 0) return [];

  const rows = Math.min(Math.ceil(count / SDM_GRID_COLS), SDM_GRID_ROWS);
  const height = GRID_SIZE / rows;

  const rowOf = (i: number) => Math.min(Math.floor(i / SDM_GRID_COLS), rows - 1);

  const rowCounts = new Array(rows).fill(0);
  for (let i = 0; i < count; i++) {
    rowCounts[rowOf(i)]++;
  }

  const rowCursor = new Array(rows).fill(0);
  const positions: GridPosition[] = [];
  for (let i = 0; i < count; i++) {
    const row = rowOf(i);
    const width = GRID_SIZE / rowCounts[row];
    const col = rowCursor[row]++;
    positions.push({ x: col * width, y: row * height, w: width, h: height });
  }
  return positions;
}

export function groupWidgetsIntoRows<T extends { position: GridPosition }>(
  widgets: readonly T[],
): T[][] {
  if (widgets.length === 0) return [];

  const sorted = [...widgets].sort(
    (a, b) =>
      Number(a.position.y) - Number(b.position.y) || Number(a.position.x) - Number(b.position.x),
  );

  const rows: T[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    const widget = sorted[i];
    const row = rows[rows.length - 1];
    const rowY = Number(row[0].position.y);
    const widgetY = Number(widget.position.y);
    if (Math.abs(rowY - widgetY) <= ROW_Y_TOLERANCE) {
      row.push(widget);
    } else {
      rows.push([widget]);
    }
  }

  return rows.map((row) => [...row].sort((a, b) => Number(a.position.x) - Number(b.position.x)));
}

/**
 * Places a new widget without moving any existing widget between rows.
 * Always grows at the bottom, left-to-right:
 * 1. Append to the bottom row when it still has room.
 * 2. Otherwise start a new bottom row when the grid has vertical space left.
 * 3. When already at SDM_GRID_ROWS, fall back to the bottom-most row that
 *    still has room (still appending at the end of that row).
 * Returns the new row grouping; callers recompute pixel positions via
 * redistributeRows.
 */
export function appendWidgetRowMajor<T>(rows: readonly T[][], widget: T): T[][] {
  const result = rows.map((row) => [...row]);
  const bottom = result[result.length - 1];

  if (bottom && bottom.length < SDM_GRID_COLS) {
    bottom.push(widget);
    return result;
  }

  if (result.length < SDM_GRID_ROWS) {
    result.push([widget]);
    return result;
  }

  for (let i = result.length - 1; i >= 0; i--) {
    if (result[i].length < SDM_GRID_COLS) {
      result[i].push(widget);
      return result;
    }
  }

  return result;
}

/** Recomputes positions; preserves row order and within-row order. */
export function redistributeRows<T extends { position: GridPosition }>(
  rows: readonly T[][],
): T[][] {
  const activeRows = rows.filter((row) => row.length > 0);
  const numRows = activeRows.length;
  const height = numRows > 0 ? GRID_SIZE / numRows : 0;

  return activeRows.map((row, rowIndex) => {
    const width = GRID_SIZE / row.length;
    return row.map((widget, col) => ({
      ...widget,
      position: { x: col * width, y: rowIndex * height, w: width, h: height },
    }));
  });
}
